// Sprint 16 A5: AI Priorities List — ranked action items with left border accent
import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import type { BriefingPriority } from "@/hooks/use-briefing-priorities";
import { SkeletonCard } from "@/components/skeleton";

const URGENCY_COLORS = {
  high: "border-l-[var(--color-accent-action)]",
  medium: "border-l-[var(--color-primary)]",
  low: "border-l-[var(--color-border)]",
};

const TYPE_LABELS: Record<BriefingPriority["type"], string> = {
  deal: "Deal", task: "Task", lead: "Lead", alert: "Alert",
};

interface AiPrioritiesListProps {
  priorities: BriefingPriority[] | null;
  isLoading: boolean;
}

export function AiPrioritiesList({ priorities, isLoading }: AiPrioritiesListProps) {
  if (isLoading || !priorities) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonCard key={i} className="bg-[var(--color-card)]" />
        ))}
      </div>
    );
  }

  if (priorities.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">
        No priorities today — you're caught up!
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {priorities.map((p, i) => (
        <Link
          key={p.id}
          to={p.actionUrl}
          className={`data-reveal data-reveal-${Math.min(i + 1, 8)} stagger-item block rounded-lg border border-[var(--color-border)] border-l-4 ${URGENCY_COLORS[p.urgency]} bg-[var(--color-card)] p-4 hover:shadow-md transition-shadow`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                  {TYPE_LABELS[p.type] ?? p.type}
                </span>
                <span className="text-sm font-semibold text-[var(--color-foreground)]">{p.title}</span>
              </div>
              <p className="text-xs text-[var(--color-muted-foreground)]">{p.description}</p>
            </div>
            <div className="flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] flex-shrink-0 ml-4">
              {p.actionLabel}
              <ArrowRight className="h-3 w-3" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
